import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Returns the window to the top whenever the history entry changes - pushes, replaces, and the
 * browser's own back/forward. Without this the browser keeps the outgoing page's scroll offset,
 * so navigating away from a scrolled-down list lands part-way down the next page.
 *
 * The document itself is what scrolls (body.css gives the app no inner scroll container), so
 * window is the correct target. Both pathname and search are watched: a query string change is a
 * click-through to a different record (e.g. `?id=`), not in-page state - selection, sorting,
 * paging and filtering are all held in component state rather than the URL.
 *
 * Deliberately useLayoutEffect, not useEffect: passive effects run after the browser paints, so
 * the new route's DOM would flash at the outgoing scroll offset for a frame before jumping to
 * the top. useLayoutEffect runs synchronously after the DOM commit but before paint, so the
 * scroll reset lands in the same paint as the content swap.
 */
export function useScrollToTop() {
  const { pathname, search } = useLocation();

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
}

// Renders nothing - mounted inside the Router purely so the hook above (which needs
// useLocation) can see history changes. App itself sits outside the Router it declares,
// so the hook can't be called directly in App's body.
export const ScrollToTop = (): null => {
  useScrollToTop();
  return null;
};
